import { useQueries } from 'react-query';
import { usePublicApiClient } from '../usePublicApiClient';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ContentfulTooltip } from '@jbhi-fi/apis/src/contentful/types';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useCustomerContext } from '../../context/CustomerContextProvider';
import { ContentfulClient } from '@jbhi-fi/apis/src/contentful/ContentfulClient';

export type UseContentfulTooltipsResult = {
  isLoading?: boolean;
  tooltips?: Array<ContentfulTooltip>;
};

export const useContentfulTooltips = ({
  contentfulThemeApiClient: contentfulThemeApiClientProp,
  contentfulClient: contentfulClientProp,
}: {
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  contentfulClient?: ContentfulClient;
}): UseContentfulTooltipsResult => {
  const { contentfulThemeApiClient: contentfulThemeApiClientContext, contentfulClient: contentfulClientContext } =
    usePublicApiClient();
  const contentfulThemeApiClient = contentfulThemeApiClientProp || contentfulThemeApiClientContext;
  const contentfulClient = contentfulClientProp || contentfulClientContext;
  if (!contentfulThemeApiClient) {
    throw new Error(
      'useContentfulTooltips must be provided with a contentfulThemeApiClient via PublicApiClientProvider or props'
    );
  }

  const isMobile = useBreakpoint('md', 'down');
  let accountType: string = 'Guest';
  const { isLoggedIn = false, isPerks = false } = useCustomerContext();
  if (isLoggedIn && !isPerks) accountType = 'Account';
  if (isLoggedIn && isPerks) accountType = 'Perks';

  const showOnValues = isMobile ? ['Mobile only', 'Desktop & Mobile'] : ['Desktop only', 'Desktop & Mobile'];
  const isPreview = window.Jbhifi?.isContentPreview || false;

  const queries = showOnValues.map(showOn => ({
    enabled: !!contentfulThemeApiClient,
    queryKey: ['useContentfulTooltips', showOn, accountType],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const baseQueryOptions = {
        content_type: 'tooltip',
        include: '1',
        'fields.showOn': showOn,
        'fields.showFor': accountType,
      };
      const shouldUseThemeApi = isPreview || !contentfulClient;
      const queryOptions = shouldUseThemeApi ? { ...baseQueryOptions, cache: 'medium' } : baseQueryOptions;

      return shouldUseThemeApi
        ? await contentfulThemeApiClient.getEntries<ContentfulTooltip>(queryOptions)
        : await contentfulClient.getEntries<ContentfulTooltip>(queryOptions);
    },
  }));

  const results = useQueries(queries);

  const isLoading = results.some(result => result.isLoading);
  const tooltipData = results.flatMap(result => result.data?.data?.items ?? []);
  return {
    isLoading,
    tooltips: tooltipData ?? [],
  };
};
